import { motion } from "framer-motion";
import { getIcon, STATUS } from "@/components/iconMap";

const ACCENTS = {
  blue: { hex: "#2E6BFF", text: "text-nc-blue", glow: "0 0 20px rgba(46, 107, 255, 0.2)" },
  orange: { hex: "#FF6B1A", text: "text-nc-orange", glow: "0 0 20px rgba(255, 107, 26, 0.2)" },
};

function TaskItem({ item, accent, phaseIndex, itemIndex }) {
  const Icon = getIcon(item.icon);
  const status = STATUS[item.status] || STATUS.todo;
  return (
    <div
      data-testid={`phase-${phaseIndex}-task-${itemIndex}`}
      className="flex flex-col gap-3 pl-4 border-l border-white/10 group/task hover:border-white/30 transition-colors duration-300"
    >
      <div className="flex items-start justify-between gap-2">
        <Icon className={`w-5 h-5 ${accent.text} shrink-0 mt-0.5`} strokeWidth={1.75} />
        <span
          data-testid={`phase-${phaseIndex}-task-${itemIndex}-status`}
          className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border w-fit"
          style={{ color: status.color, borderColor: `${status.color}55`, backgroundColor: `${status.color}11` }}
        >
          {status.label}
        </span>
      </div>
      <h4 data-testid={`phase-${phaseIndex}-task-${itemIndex}-title`} className="font-sans font-semibold uppercase text-base leading-tight text-zinc-100">
        {item.title}
      </h4>
      <p className="font-mono text-xs text-zinc-500 leading-relaxed">{item.description}</p>
      <div className="mt-auto pt-2">
        <div className="flex justify-between items-baseline mb-1.5">
          <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-600 uppercase">Progress</span>
          <span data-testid={`phase-${phaseIndex}-task-${itemIndex}-progress`} className="font-mono text-[10px] text-zinc-400">
            {item.progress}%
          </span>
        </div>
        <div className="h-1 w-full bg-white/10 overflow-hidden relative">
          <motion.div
            className="absolute inset-y-0 left-0"
            style={{ backgroundColor: accent.hex, boxShadow: accent.glow }}
            initial={{ width: 0 }}
            whileInView={{ width: `${item.progress}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PhaseCard({ phase, index }) {
  const accent = ACCENTS[phase.accent] || ACCENTS.blue;
  const phaseProgress = phase.items.length
    ? Math.round(phase.items.reduce((s, i) => s + i.progress, 0) / phase.items.length)
    : 0;
  const HeaderIcon = getIcon(phase.items[0]?.icon);

  return (
    <motion.section
      data-testid={`phase-card-${index}`}
      className="relative border border-white/10 bg-black/40 backdrop-blur-md flex flex-col md:flex-row items-stretch overflow-hidden"
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent.hex, boxShadow: accent.glow }} />

      <div className="p-6 md:p-8 md:w-[28%] border-b md:border-b-0 md:border-r border-white/10 flex flex-col justify-center relative">
        <span className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Phase</span>
        <span
          data-testid={`phase-${index}-number`}
          className={`font-sans font-bold text-6xl lg:text-7xl leading-none ${accent.text} mt-1`}
        >
          {phase.number}
        </span>
        <h3 data-testid={`phase-${index}-title`} className="font-sans font-bold uppercase text-xl lg:text-2xl text-zinc-50 mt-4 leading-tight">
          {phase.title}
        </h3>
        <p className={`font-mono text-[10px] tracking-[0.25em] uppercase mt-2 ${accent.text}`}>{phase.tagline}</p>
        {phase.targetDate && (
          <p data-testid={`phase-${index}-target-date`} className="font-mono text-[10px] tracking-[0.25em] uppercase mt-3 text-zinc-300 border border-white/15 w-fit px-2 py-1 bg-white/5">
            Target // {phase.targetDate}
          </p>
        )}
        <p className="font-mono text-xs text-zinc-500 mt-4 leading-relaxed">{phase.description}</p>
        <div className="mt-6 flex items-center gap-3">
          <HeaderIcon className={`w-8 h-8 ${accent.text} opacity-40`} strokeWidth={1.25} />
          <span data-testid={`phase-${index}-progress`} className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase ml-auto">
            {phaseProgress}% Complete
          </span>
        </div>
      </div>

      <div className="p-6 md:p-8 md:w-[72%] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {phase.items.map((item, ii) => (
          <TaskItem key={item.id} item={item} accent={accent} phaseIndex={index} itemIndex={ii + 1} />
        ))}
      </div>
    </motion.section>
  );
}
